import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap'
import { chatsAPI } from '../../services/chatsApi'
import { stageActsAPI } from '../../services/api'
import ChatSectionHeader from './ChatSectionHeader'

const formatDate = (timestamp) => (timestamp ? new Date(timestamp).toLocaleDateString('ru-RU') : '')

const formatSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`
}

const ChatStageActs = () => {
  const { id, stageId } = useParams()
  const fileInputRef = useRef(null)
  const [stage, setStage] = useState(null)
  const [acts, setActs] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const loadStage = useCallback(async () => {
    try {
      setLoading(true)
      const response = await chatsAPI.stage(id, stageId)
      setStage(response.data)
      setActs(response.data.acts)
      setError(null)
    } catch (err) {
      console.error('Error loading stage acts:', err)
      setError(err.response?.status === 403 ? 'У вас нет доступа к этому чату' : 'Не удалось загрузить акты')
    } finally {
      setLoading(false)
    }
  }, [id, stageId])

  useEffect(() => {
    loadStage()
  }, [loadStage])

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0 || !stage) return

    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        const response = await stageActsAPI.create(stage.project_id, stage.id, formData)
        setActs((prev) => [...prev, response.data])
      }
      setError(null)
    } catch (err) {
      console.error('Error uploading act:', err)
      setError('Не удалось загрузить файл')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleTogglePaid = async (act) => {
    try {
      const response = await stageActsAPI.togglePaid(stage.project_id, stage.id, act.id)
      setActs((prev) => prev.map((a) => (a.id === act.id ? { ...a, paid: response.data.paid } : a)))
    } catch (err) {
      console.error('Error toggling paid:', err)
      setError('Не удалось изменить статус оплаты')
    }
  }

  const handleDelete = async (act) => {
    if (!window.confirm(`Удалить «${act.name}»?`)) return

    try {
      await stageActsAPI.delete(stage.project_id, stage.id, act.id)
      setActs((prev) => prev.filter((a) => a.id !== act.id))
    } catch (err) {
      console.error('Error deleting act:', err)
      setError('Не удалось удалить акт')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error && !stage) {
    return <Alert variant="danger">{error}</Alert>
  }

  const unpaidCount = acts.filter((a) => !a.paid).length

  return (
    <div>
      <ChatSectionHeader
        title={`Этап ${stage.number}. Акты`}
        subtitle={stage.name}
        backTo={`/chats/${id}/stages/${stageId}`}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="d-none"
          onChange={handleUpload}
        />
        <Button variant="primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
          {uploading ? 'Загрузка...' : 'Добавить файл'}
        </Button>
        {acts.length > 0 && (
          <span className="text-muted small">
            Всего: {acts.length}
            {unpaidCount > 0 && ` · не оплачено: ${unpaidCount}`}
          </span>
        )}
      </div>

      {acts.length === 0 ? (
        <Alert variant="light" className="border text-center text-muted">
          Актов пока нет. Нажмите «Добавить файл», чтобы загрузить готовый акт или КП.
        </Alert>
      ) : (
        <Card className="shadow-sm border-0">
          <div className="list-group list-group-flush">
            {acts.map((act) => (
              <div key={act.id} className="list-group-item py-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <a
                      href={act.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="fw-semibold d-block text-truncate text-decoration-none"
                    >
                      {act.name}
                    </a>
                    <div className="text-muted small">
                      {formatDate(act.created_at)}
                      {act.file_size ? ` · ${formatSize(act.file_size)}` : ''}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <Badge
                      bg={act.paid ? 'success' : 'warning'}
                      text={act.paid ? undefined : 'dark'}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleTogglePaid(act)}
                      title="Нажмите, чтобы изменить статус оплаты"
                    >
                      {act.paid ? 'Оплачен' : 'Не оплачен'}
                    </Badge>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(act)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

export default ChatStageActs
